import { ComponentChildren, Fragment, h, Ref, RefObject } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { Spacer, Toolbar, ToolIcon } from "uitoolkit";
import "./chatinput.scss";
import { useMeasureElement } from "hooks";

// todo: support for turning the send-button into a stop-button
// todo: support for disabling it

// todo: support for a "fullscren" icon that only is visible if the input has grown beyond the max height
// WIP: clicks inside the chatinput but outside the textarea and any buttons etc, should focus the textarea
// WIP: pressing ESC while another element inside has focus, should shift focus to the textarea

export function ChatInput(props: {
    toolsTop?: ComponentChildren,
    toolsLeft?: ComponentChildren,
    toolsRight?: ComponentChildren,

    onSend?: (message: string) => void,
    onStop?: () => void;
    placeHolder?: string,

    minVisibleLines?: number,
    maxVisibleLines?: number,

    buttonType?: "send" | "stop";
    disableButton?: boolean,

    inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement>;
}) {
    const _inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
    const inputRef = props.inputRef ?? _inputRef;

    const lineHeight = 20; // px
    const minHeight = 48; // px for single line
    const isMultiLine = (props.minVisibleLines ?? 1) > 1 || (props.maxVisibleLines ?? 1) > 1;
    const [height, setHeight] = useState(48 + (Math.max(0, (props.minVisibleLines ?? 1) - 1) * lineHeight));

    // Get or create the reusable measurement element
    const measureDiv = useMeasureElement();

    const resizeOnInput = useCallback((e: Event) => {
        if (!isMultiLine) return; // Skip resizing if multiline is disabled

        const textarea = e.target as HTMLTextAreaElement;
        const minLines = props.minVisibleLines ?? 1;
        const maxLines = props.maxVisibleLines ?? 8;

        // Update the measurement element properties
        measureDiv.style.fontSize = '16px';
        measureDiv.style.lineHeight = '20px';
        measureDiv.style.width = textarea.offsetWidth + 'px';
        measureDiv.style.fontFamily = window.getComputedStyle(textarea).fontFamily;

        // Handle trailing newlines properly by ensuring they create visible lines
        let text = textarea.value;
        if (text.endsWith('\n')) {
            text += ' '; // Add a space to make the empty line visible
        }
        measureDiv.textContent = text;

        // Get the content height
        const contentHeight = measureDiv.offsetHeight;

        // Calculate lines needed (minimum 1 line)
        const lines = Math.max(1, Math.ceil(contentHeight / lineHeight));
        const clampedLines = Math.max(minLines, Math.min(lines, maxLines));

        // Calculate the target height
        // For 1 line: 48px
        // For 2+ lines: 48px + (lines-1) * 20px
        const targetHeight = minHeight + (clampedLines - 1) * lineHeight;

        setHeight(targetHeight);
    }, [measureDiv, props.minVisibleLines, props.maxVisibleLines]);

    const onButton = (e?: Event) => {
        e?.preventDefault();
        if (props.disableButton) return;
        if (props.buttonType == "stop") {
            props.onStop?.();
        } else {
            if (inputRef.current?.value) {
                props.onSend?.(inputRef.current.value);
                inputRef.current.value = '';
            }
        }
    }

    const handleInput = useCallback((e: Event) => {
        const textarea = e.target as HTMLTextAreaElement;
        e.stopImmediatePropagation();

        // Handle Enter key to send message
        if (e instanceof KeyboardEvent && e.key === 'Enter' && !e.shiftKey) {
            onButton(e);
            return;
        }

        // Filter newlines in single-line mode
        if (!isMultiLine && textarea.value.includes('\n')) {
            const cursorPos = textarea.selectionStart;
            const newValue = textarea.value.replace(/\n/g, '');
            const removedChars = textarea.value.length - newValue.length;
            textarea.value = newValue;
            // Adjust cursor position for removed characters
            textarea.setSelectionRange(cursorPos - removedChars, cursorPos - removedChars);
        }

        // Auto-resize if enabled
        if (isMultiLine) {
            resizeOnInput(e);
        }
    }, [props.minVisibleLines, props.maxVisibleLines, props.onSend, resizeOnInput, onButton]);

    const focusChat = (e: h.JSX.TargetedMouseEvent<HTMLDivElement>) => {
        if (e.target instanceof HTMLSelectElement || e.target instanceof HTMLInputElement) {
            return;
        }
        inputRef.current?.focus();
    }

    const focusChat2 = (e: h.JSX.TargetedKeyboardEvent<HTMLDivElement>) => {
        if (e.code == "Escape") {
            inputRef.current?.focus();
        }
    }

    const buttonType = props.buttonType ?? "send";
    const icon = buttonType == "stop" ? "stop-circle" : "send";
    //const icon = "teams-send-filled";

    return (
        <div class="component4-chatinput no-print" onClick={focusChat} onKeyDown={focusChat2}>
            {
                props.toolsTop &&
                <div class="tool-row">
                    <Toolbar class="component-form horizontal grow-0">
                        {props.toolsTop}
                    </Toolbar>
                </div>
            }
            <div class="editor-row">
                {
                    !isMultiLine && props.toolsLeft &&
                    <Toolbar class="component-form horizontal grow-0">
                        {
                            props.toolsLeft
                        }
                    </Toolbar>
                }
                {
                    !isMultiLine &&
                    <input
                        ref={inputRef as Ref<HTMLInputElement>}
                        type="text"
                        class="editor"
                        autoFocus
                        placeholder={props.placeHolder ?? "Type a message"}
                        onKeyDown={handleInput}
                    />
                }
                {
                    isMultiLine &&
                    <textarea
                        ref={inputRef as Ref<HTMLTextAreaElement>}
                        class="editor"
                        autoFocus
                        placeholder={props.placeHolder ?? "Type a message"}
                        onInput={handleInput}
                        onKeyDown={handleInput}
                        style={{ height: height + "px" }}
                    />
                }
                {
                    !isMultiLine &&
                    <Toolbar class="component-form horizontal grow-0">
                        {
                            props.toolsRight
                        }
                        <ToolIcon icon={icon} onClick={onButton} class="circle" disabled={props.disableButton} />
                    </Toolbar>
                }
            </div>
            {
                isMultiLine && (props.toolsLeft || props.toolsRight) &&
                <div class="tool-row">
                    <Toolbar class="component-form horizontal">
                        {props.toolsLeft}
                        <Spacer />
                        {props.toolsRight}
                        <ToolIcon icon={icon} onClick={onButton} class="circle" disabled={props.disableButton} />
                    </Toolbar>
                </div>
            }
        </div>
    )
}
